import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { UserRole } from './role.service';
import { mapToUserRoles } from '../utils/jwt-roles.util';
import { environment } from '../../../environments/environment';

export interface TokenResponse {
  accessToken: string;
  refreshToken?: string;
  roles?: UserRole[];
  isTwoFactorEnabled?: boolean;
}

export interface TwoFactorChallengeResponse {
  requiresTwoFactor: true;
  challengeId: string;
  expiresAt: string;
}

/** Response when the account must set a new password before continuing. */
export interface MustChangePasswordResponse {
  mustChangePassword: true;
}

/** Response from POST /api/Auth/login. */
type LoginApiResponse = {
  accessToken: string;
  isTwoFactorEnabled: boolean;
} | TwoFactorChallengeResponse | MustChangePasswordResponse;

/** Response from POST /api/Auth/refreshToken. */
interface RefreshApiResponse {
  accessToken: string;
  refreshToken: string;
}

/** Typed HTTP client for authentication endpoints; session state is owned by AuthEffects. */
@Injectable({
  providedIn: 'root',
})
export class AuthenticationService {
  constructor(private readonly http: HttpClient) {}

  /**
   * Realiza el login del User.
   * TODO: Reemplazar por llamada real a la API; los roles deben venir del backend.
   * @param email - Email del User
   * @param password - Contrasena del User
   */
  login(email: string, password: string): Observable<TokenResponse | TwoFactorChallengeResponse | MustChangePasswordResponse> {
    return this.http
      .post<LoginApiResponse>(`${environment.apiUrl}/api/Auth/login`, { email, password })
      .pipe(
        map((response) => {
          if ('requiresTwoFactor' in response || 'mustChangePassword' in response) {
            return response;
          }
          const roles = this.extractRolesFromToken(response.accessToken);
          return { accessToken: response.accessToken, roles, isTwoFactorEnabled: response.isTwoFactorEnabled };
        }),
      );
  }

  /**
   * Completes a mandatory password change for an account whose password was set by the
   * system (for example, a donor's temporary password sent by email).
   * @param email - Account email.
   * @param currentPassword - The temporary password used to prove identity.
   * @param newPassword - The new password to set for the account.
   */
  changePassword(email: string, currentPassword: string, newPassword: string): Observable<void> {
    return this.http.post<void>(`${environment.apiUrl}/api/Auth/change-password`, {
      email,
      currentPassword,
      newPassword,
    });
  }

  completeTwoFactorChallenge(challengeId: string, otpOrRecoveryCode: string): Observable<TokenResponse> {
    return this.http
      .post<LoginApiResponse>(`${environment.apiUrl}/api/Auth/2fa/challenge/complete`, {
        challengeId,
        otpOrRecoveryCode,
      })
      .pipe(
        map((response) => {
          if ('requiresTwoFactor' in response || 'mustChangePassword' in response) {
            throw new Error('Unexpected two-factor challenge response');
          }
          return {
            accessToken: response.accessToken,
            roles: this.extractRolesFromToken(response.accessToken),
            isTwoFactorEnabled: response.isTwoFactorEnabled,
          };
        }),
      );
  }

  /**
   * Renueva el token de acceso usando el refresh token almacenado.
  * Los efectos managementan la persistencia y el fallo de la renovacion.
   */
  refreshToken(accessToken: string | null): Observable<TokenResponse> {
    return this.http
      .post<RefreshApiResponse>(`${environment.apiUrl}/api/Auth/refreshToken`, {
        accessToken,
      })
      .pipe(
        map((response) => ({
          accessToken: response.accessToken,
          refreshToken: response.refreshToken,
        })),
      );
  }

  private extractRolesFromToken(token: string): UserRole[] {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return mapToUserRoles(payload['Role']);
    } catch {
      return [];
    }
  }

}
